import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'reactstrap';
import {
  Overlay,
  Card,
  Header,
  Body,
  Actions,
  TextInput,
  ListBox,
  Row
} from './style';

import { useConectionSocketChatInternal } from 'hooks/useInternalChatSocket';
import { apiUc } from '../../../../http/index';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { buttonStyle } from 'components/Containers/ConfigurationContainer/components/utils';

export default function CreateGroupModal({
  isOpen,
  onClose,
  headerColor = '#ff2b80',
  headerFontColor = '#ffffff'
}) {
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [usersRelatedByTeam, setUsersRelatedByTeam] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const systemConfiguration = useSelector((state) => state.systemConfiguration);

  const { socketInternalChat } = useConectionSocketChatInternal();
  const prefix = 'group';

  const getUserId = (u) => u.login ?? u.id ?? u.user_id;
  const getUserName = (u) => u.name ?? u.nome ?? u.full_name ?? u.login;

  useEffect(() => {
    if (!isOpen) return;

    const fetchUsers = async () => {
      try {
        setLoading(true);
        const login = localStorage.getItem('login');
        if (!login) return;

        const { data } = await apiUc.get(`/api/teams-users/${login}`);
        const users = data.data || [];

        const filteredUsers = users.filter((u) => getUserId(u) !== login);

        setUsersRelatedByTeam(filteredUsers);
      } catch (err) {
        toast.error(
          'Erro ao carregar usuários relacionados ao time. (fetchUsers)',
          {
            autoClose: 3000,
            closeOnClick: true
          }
        );
        console.error('Erro ao carregar usuários relacionados ao time:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [isOpen]);

  function handleCreateGroup({ name, members }) {
    const creator = localStorage.getItem('login');
    if (!creator) return;

    setSubmitting(true);

    const allMembers = Array.from(new Set([creator, ...members]));

    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      setSubmitting(false);
      reset();
      onClose();
    };

    socketInternalChat.emit(`${prefix}:create`, {
      creator,
      title: name,
      members: allMembers
    });

    socketInternalChat.once(`${prefix}:create:success`, () => {
      finish();
    });

    const onError = (err) => {
      toast.error('Erro na criação do grupo (socket):', {
        autoClose: 3000,
        closeOnClick: true
      });
      console.error('Erro na criação do grupo (socket):', err);
      finish();
    };

    socketInternalChat.once(`${prefix}:create:error`, onError);

    setTimeout(() => {
      if (!finished) {
        toast.warn(
          'Não foi possível criar o grupo. Tente novamente mais tarde. (Timeout na criação do grupo)',
          { autoClose: 3000, closeOnClick: true }
        );
        console.warn('Timeout na criação do grupo');
        finish();
      }
    }, 5000);
  }

  const filtered = useMemo(() => {
    return (Array.isArray(usersRelatedByTeam) ? usersRelatedByTeam : []).filter(
      (u) => getUserName(u)?.toLowerCase().includes(query.toLowerCase())
    );
  }, [usersRelatedByTeam, query]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const reset = () => {
    setName('');
    setQuery('');
    setSelected(new Set());
  };

  const submit = () => {
    if (!name.trim() || selected.size === 0) return;
    handleCreateGroup({ name: name.trim(), members: Array.from(selected) });
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Header color={headerColor} fontColor={headerFontColor}>
          <span>Criar grupo</span>
          <button onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </Header>

        <Body>
          <div>
            <small>Nome do grupo</small>
            <TextInput
              placeholder="Ex.: Suporte Comercial"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              autoFocus
            />
          </div>

          <div>
            <small>Participantes</small>
            <TextInput
              placeholder="Buscar pessoas…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <ListBox>
            {loading && (
              <div style={{ padding: '12px', color: '#888' }}>
                Carregando usuários...
              </div>
            )}
            {!loading &&
              filtered.map((u) => {
                const id = getUserId(u);
                const checked = selected.has(id);
                return (
                  <Row key={id}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(id)}
                    />
                    <span>{getUserName(u)}</span>
                  </Row>
                );
              })}
            {!loading && filtered.length === 0 && (
              <div style={{ padding: '12px', color: '#888' }}>
                Nenhum resultado
              </div>
            )}
          </ListBox>
        </Body>

        <Actions>
          <Button color="danger" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            style={buttonStyle(systemConfiguration.primary_color)}
            disabled={submitting || !name.trim() || selected.size === 0}
            onClick={submit}
          >
            {submitting ? 'Criando...' : 'Criar grupo'}
          </Button>
        </Actions>
      </Card>
    </Overlay>
  );
}
